// Libraries import
import { useState } from "react";
import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from "axios";

// Service import
import { useSetUserAuthInfo } from "./useSetUserAuthInfo";

// Interfaces declaration
interface IFunctionReturn {
  axiosGetRequest: <T>(
    URL: string,
    config?: AxiosRequestConfig | undefined
  ) => Promise<AxiosResponse<T> | void>;
  isLoading: boolean;
  errorMessage: string;
}

// useAxiosPost : return tools to send GET request and handle error messages
// Parameters
// ----------
// RAS
//
// Returns
// ----------
// Function axiosGetRequest
//     Function to call to send GET request
// Boolean isLoading
//     Indicate if the asynchronous GET request is onGoing
// String errorMessage
//     GET request error message (if request fails)

export const useAxiosGet = (): IFunctionReturn => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const { setUserAuthInfo } = useSetUserAuthInfo();

  // axiosGetRequest : send GET request and handle error cases
  // Parameters
  // ----------
  // URL: String
  //      the URL of the API
  // config (optional): Object
  //      contain configuration information to send to the API
  // Returns
  // ----------
  // RAS

  const axiosGetRequest = async <T>(
    URL: string,
    config?: AxiosRequestConfig | undefined
  ): Promise<AxiosResponse<T> | void> => {
    setIsLoading(true);
    setErrorMessage("");
    try {
      const response = await axios.get<T>(URL, config);

      // client received an successfull response (2xx)
      if (response.status.toString()[0] === "2") {
        setIsLoading(false);
        return response;
      }
      setIsLoading(false);
      setErrorMessage("Une erreur est survenue, merci de réessayer.");
      return;
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      // client received an error response (5xx, 4xx)
      if (axiosError.response) {
        if (axiosError.response.status === 401) {
          setErrorMessage("Une erreur est survenue, merci de réessayer.");
          // If response is unauthorized we reset user authorization info -> redirect the user to the login page
          setUserAuthInfo(null);
          setIsLoading(false);
          return;
        }
        if (axiosError.response.status === 422) {
          // 422 is a specific error status where the error message is defined by the CRM
          // Format of the data is : { "status": int,"errors": [[]]}
          const textError: string | undefined =
            axiosError.response?.data?.errors;
          setErrorMessage(
            textError || "Une erreur est survenue, merci de réessayer."
          );
        }
        setIsLoading(false);
        return;
      } else if (axiosError.request) {
        // client never received a response, or request never left
        if (typeof axiosError.request.data === "string") {
          setErrorMessage(axiosError.request.data);
          setIsLoading(false);
          return;
        }
        setErrorMessage("Erreur serveur, merci de réessayer.");
        setIsLoading(false);
        return;
      } else {
        setErrorMessage("Une erreur est survenue, merci de réessayer.");
      }
      setIsLoading(false);
    }
  };

  return { axiosGetRequest, isLoading, errorMessage };
};
